import { writable, get } from 'svelte/store';
import type { Writable, Updater } from 'svelte/store';
import { jwtDecode } from 'jwt-decode';
import site from './site';

// Types
export interface TokenData {
	lastName: string;
	firstName: string;
	userUUID: string;
	username: string;
	email: string;
	isActive: boolean;
	roles: { name: string; roleUUID: string }[];
	iat: number;
	exp: number;
}

/**
 * What AbstractID answers a sign-in attempt with (Design §14).
 *
 * `token` and `twoFactorToken` are mutually exclusive and `requiresTwoFactor` says which arrived:
 * a full session, or a short-lived token that buys one more question. Treating a missing `token`
 * as a failure would report a correct code as a wrong one to every account with TOTP enabled.
 */
export interface AuthResult {
	token?: string;
	twoFactorToken?: string;
	requiresTwoFactor?: boolean;
}

export interface UserProfile {
	lastName: string;
	firstName: string;
	userUUID: string;
	email: string;
	profilePictureURL?: string;
}

/**
 * Writable store backed by localStorage.
 *
 * Values are JSON-encoded by default, so any serialisable type works — booleans
 * for collapsed sidebar sections, objects for view preferences. Pass
 * `storeAsJson = false` for plain strings written by other code (e.g. the
 * `color-theme` key, which is also read by the pre-hydration theme script).
 */
export function storable<T>(key: string, data: T, storeAsJson: boolean = true): Writable<T> {
	const store: Writable<T> = writable(data);

	const stored = localStorage.getItem(key);
	if (stored != null) {
		store.set(storeAsJson ? JSON.parse(stored) : (stored as unknown as T));
	}

	return {
		subscribe: store.subscribe,
		set: (value: T) => {
			localStorage.setItem(key, storeAsJson ? JSON.stringify(value) : `${value}`);
			store.set(value);
		},
		update: (fnCallback: Updater<T>) => {
			const updatedStore: T = fnCallback(get(store));

			localStorage.setItem(key, storeAsJson ? JSON.stringify(updatedStore) : `${updatedStore}`);
			store.set(updatedStore);
		}
	};
}

// Stores
export const isAuthorized = writable(false);
export const isAuthorizationPending = writable(true);
export const colorTheme = storable<'dark' | 'light'>('color-theme', 'dark', false);
export const userProfileAvailable = writable(false);

/**
 * Routes a visitor may reach with no token at all (Design §14, D9).
 *
 * The template's client cannot browse anonymously: `initialize()` redirects to AbstractID
 * whenever no token is stored, so a first-time visitor to `/Product/polyverse-pro` is bounced to
 * the identity provider before any catalog renders — and a storefront nobody can look at without
 * signing in is not a storefront.
 *
 * A `*` matches one or more path segments. Everything **not** listed keeps the inherited
 * behaviour and redirects, which is what serves as the route guard for `/Account/*` and
 * `/Settings/Admin/*` — a guard that lives in one list rather than in every page.
 *
 * This failure is invisible during development, where the developer is always already signed in.
 *
 * There is deliberately **no sign-in page of our own**, and no sign-in step on a checkout page
 * either. The storefront is a catalogue anyone may read; the one place it asks who somebody is is
 * inside the purchase confirmation window, where the customer can still see what they are buying
 * (§14). Everywhere else — the sidebar control, the coupon field — a regular sign-in hands off to
 * the identity provider, which is the thing that actually owns identity.
 *
 * So the catalogue is public and **both checkout pages are not**. `/Checkout/Start` and
 * `/Checkout/Return` exist only to call `requireAuth` endpoints about a purchase whose customer is
 * already signed in by the time either is reached, so an arrival with no token is a stale link or
 * a typed URL, has nothing to render, and the inherited redirect is the correct guard.
 * `/Checkout/Canceled` is public for the opposite reason: the provider redirects there as a full
 * page load, and demanding a sign-in to be told that nothing was charged is the worst possible
 * moment to ask.
 */
export const publicRoutes: string[] = [
	'/',
	'/Products',
	'/Products/*',
	'/Product/*',
	'/Checkout/Canceled'
];

/** Turns a route pattern into an anchored regular expression. `*` spans path segments. */
function patternToRegExp(pattern: string): RegExp {
	const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.+');
	return new RegExp(`^${escaped}/?$`);
}

/**
 * Whether a path is browsable signed out. Trailing slashes are tolerated, because a link with one
 * is the same page and bouncing it to the login screen would be a bug nobody could explain.
 */
export function isPublicPath(pathname: string): boolean {
	const path = pathname.length === 0 ? '/' : pathname;
	return publicRoutes.some(pattern => patternToRegExp(pattern).test(path));
}

// Token storage key
const TOKEN_KEY = 'jwt2';

/**
 * Builds an AbstractID authorization URL.
 *
 * The provider's `/authorize` page requires BOTH parameters and refuses the
 * request without them ("Missing application context" / "Return URL is
 * required for authorization"). The parameter names are `app` and `returnURL`
 * — see AbstractID's `pageComponents/auth/Authorize.svelte`, which reads them
 * verbatim. Constructed in one place so the two callers — sign-in and
 * sign-out — can never drift apart.
 *
 * Do NOT resurrect the old `/login?verification_url=…` form: AbstractID only
 * still honours it through a legacy shim in its `index.html` that rewrites it
 * onto this URL.
 *
 * @param returnURL Absolute URL AbstractID sends the user back to, with the token appended.
 * @param extra Additional query parameters, e.g. `{ logout: 'true' }` to revoke the session.
 */
function makeAuthorizeURL(returnURL: string, extra: Record<string, string> = {}): string {
	const params = new URLSearchParams({
		app: site.appID,
		returnURL
	});

	for (const [key, value] of Object.entries(extra)) {
		params.set(key, value);
	}

	return `${site.idBaseURL}/authorize?${params.toString()}`;
}

/** Query parameter AbstractID appends to returnURL to hand the JWT back. */
const TOKEN_PARAM = 'token';

/**
 * Builds the URL AbstractID sends the user back to after authenticating.
 *
 * Keeps origin + path + query, drops the hash. The router is history-mode
 * (`Router.svelte` `getLocation()` reads `window.location.pathname`), so the
 * path and query ARE the current route — preserving them is what makes deep
 * links survive the login round-trip.
 *
 * The hash must go, and this is the subtle part: AbstractID appends the token
 * to the END of returnURL (`Authorize.svelte`: `returnURL + separator +
 * 'token=' + …`). With a hash still attached the append would land inside the
 * fragment — `…/Users#x?token=…` — leaving `window.location.search` empty, so
 * `initialize()` would never see the token and would bounce back to login in a
 * loop. Any stale `token` is stripped for the same reason it exists: it was
 * put there by the previous hand-off.
 */
function makeReturnURL(url: string): string {
	try {
		const parsed = new URL(url, document.location.origin);
		parsed.hash = '';
		parsed.searchParams.delete(TOKEN_PARAM);
		return parsed.toString();
	} catch {
		return site.baseURL;
	}
}

// AbstractID module
const AbstractID = {
	tokenData: null as TokenData | null,
	userProfilePictureURL: '' as string,
	refreshInterval: null as ReturnType<typeof setInterval> | null,

	get isAdministrator(): boolean {
		if (!this.tokenData) return false;
		if (!site.adminRole || site.adminRole.length === 0) return false;
		return this.tokenData.roles?.some(r => r.name === site.adminRole) ?? false;
	},

	get isUserApplicationAccessRoleAvailable(): boolean {
		if (!site.accessRole || site.accessRole.length === 0) return true;
		return this.tokenData?.roles?.some(r => r.name === site.accessRole) ?? false;
	},

	getToken(): string | null {
		return localStorage.getItem(TOKEN_KEY);
	},

	setToken(tokenStringValue: string): void {
		try {
			this.tokenData = jwtDecode<TokenData>(tokenStringValue);
			localStorage.setItem(TOKEN_KEY, tokenStringValue);

			// Fetch profile picture if not already available
			if (!get(userProfileAvailable)) {
				fetch(`${site.backend.idBaseURL}/user-backend/user/${this.tokenData!.userUUID}/profile`)
					.then((response) => response.json())
					.then((profileJson) => {
						if (profileJson.data?.profilePictureURL?.length > 0) {
							this.userProfilePictureURL = profileJson.data.profilePictureURL;
							userProfileAvailable.set(true);
						}
					})
					.catch(() => {});
			}
		} catch {
			this.tokenData = null;
			localStorage.removeItem(TOKEN_KEY);
		}
	},

	clearToken(): void {
		localStorage.removeItem(TOKEN_KEY);
		this.tokenData = null;
	},

	/**
	 * The one place this app leaves for another origin.
	 *
	 * Behind a method rather than inline, because `window.location` is an *unforgeable* interface:
	 * neither it nor its `href` can be redefined, so there is no way to observe an assignment to
	 * it from outside. Every sign-in and sign-out redirect goes through here, which is what makes
	 * "did the storefront bounce an anonymous visitor to the identity provider" a question a test
	 * can ask at all — and that is the single regression this whole rework exists to prevent.
	 */
	navigateTo(url: string): void {
		window.location.href = url;
	},

	navigateToLogin(redirectURL?: string): void {
		this.navigateTo(makeAuthorizeURL(makeReturnURL(redirectURL ?? document.location.href)));
	},

	/**
	 * The **explicit** sign-in, replacing the implicit redirect `initialize()` used to perform.
	 *
	 * Called by the sign-in control and by Buy. `returnTo` is a path within this app; it is
	 * resolved against the origin and then run through `makeReturnURL()`, which drops the hash —
	 * without that the token AbstractID appends lands inside the fragment, `window.location.search`
	 * comes back empty, and `initialize()` bounces back to login forever (see `CLAUDE.md`).
	 *
	 * Anything secret about the purchase — the coupon code above all — must stay out of
	 * `returnTo`: it becomes a query parameter on the identity provider, where it is logged
	 * (Design §14). Step 5 passes only a `sessionStorage` nonce.
	 */
	beginSignIn(returnTo?: string): void {
		const destination =
			returnTo === undefined ? document.location.href : new URL(returnTo, document.location.origin).toString();
		this.navigateTo(makeAuthorizeURL(makeReturnURL(destination)));
	},

	/**
	 * User-initiated sign-out. Clears the local token and hands off to AbstractID
	 * with `logout=true` so the session is revoked there too — otherwise the next
	 * sign-in silently reuses the old identity instead of prompting.
	 */
	signOut(): void {
		if (this.refreshInterval) {
			clearInterval(this.refreshInterval);
			this.refreshInterval = null;
		}
		this.clearToken();
		isAuthorized.set(false);
		this.navigateTo(makeAuthorizeURL(site.baseURL, { logout: 'true' }));
	},

	refreshToken(onError?: (error: { status: number; statusText: string }) => void): void {
		const tokenString = this.getToken();
		if (!tokenString) return;

		const xhr = new XMLHttpRequest();
		xhr.open("POST", `${site.backend.idBaseURL}/user-backend/auth/refresh`);
		xhr.setRequestHeader('Content-type', 'application/json');
		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				const newToken = JSON.parse(xhr.response);
				if (newToken?.data?.accessToken?.length > 0) {
					this.setToken(newToken.data.accessToken);
				}
			} else {
				if (onError) onError({ status: xhr.status, statusText: xhr.statusText });
			}
		};
		xhr.onerror = () => {
			if (onError) onError({ status: xhr.status, statusText: xhr.statusText });
		};
		xhr.send(JSON.stringify({ token: tokenString }));
	},

	/**
	 * Starts the ten-second refresh loop, cancelling any loop already running.
	 *
	 * Extracted from `initialize()` because the inline sign-in of Design §14 arrives at an
	 * authenticated state by a second route: `completeSignIn()` is handed a token by AbstractID's
	 * OTP endpoint rather than finding one in the URL, and a session that never refreshes expires
	 * mid-purchase with nothing to say for itself.
	 *
	 * @param onExpired Run when the provider refuses to refresh — the token is gone for good.
	 */
	startTokenRefresh(onExpired: () => void): void {
		const tokenRefreshIntervalInSeconds = 10;

		const fnCancelAutomaticTokenRefresh = () => {
			if (this.refreshInterval) {
				clearInterval(this.refreshInterval);
				this.refreshInterval = null;
			}
		};

		const onTokenError = () => {
			fnCancelAutomaticTokenRefresh();
			onExpired();
		};

		fnCancelAutomaticTokenRefresh();
		this.refreshInterval = setInterval(() => {
			this.refreshToken(onTokenError);
		}, tokenRefreshIntervalInSeconds * 1000);
		this.refreshToken(onTokenError);
	},

	/**
	 * Adopts a token obtained without leaving the storefront, and resolves the app to signed in.
	 *
	 * The counterpart to the `?token=` hand-off `initialize()` performs: same storage, same refresh
	 * loop, same stores — the only difference is where the token came from. It is *not* merged into
	 * `initialize()`, which is a boot-time routine that also decides whether to redirect; calling it
	 * again after a sign-in would re-read a URL that no longer describes the situation.
	 *
	 * `authorizeApplication()` is awaited rather than fired and forgotten, because the caller
	 * navigates immediately afterwards and a full page load cancels an in-flight request. Its
	 * failure is swallowed: the customer is signed in either way, and refusing the sign-in over a
	 * bookkeeping call at the identity provider would strand a purchase that is otherwise complete.
	 */
	async completeSignIn(token: string): Promise<void> {
		this.setToken(token);

		if (!this.tokenData) throw new Error('The sign-in token could not be read.');

		this.startTokenRefresh(() => {
			this.clearToken();
			isAuthorized.set(false);
		});

		isAuthorized.set(true);
		isAuthorizationPending.set(false);
		this.applyTheme();

		try {
			await this.authorizeApplication();
		} catch {
			/* See above — the sign-in stands regardless. */
		}
	},

	/**
	 * POSTs to AbstractID and unwraps its `{ status, data }` envelope.
	 *
	 * A refusal is thrown carrying **the provider's own sentence**, because that sentence is the
	 * only thing that distinguishes the failures a customer can act on: "Invalid or expired OTP."
	 * is a different instruction from "Too many requests". The three places it can sit are all
	 * checked, in the order AbstractID's own client checks them — the middleware answers
	 * `{ error }`, some handlers nest a `message` under `data`, and a proxy or gateway in front of
	 * the provider may answer neither. Only when all three are absent does a sentence of ours
	 * stand in, and it names the status so the failure is at least identifiable.
	 */
	async postToIdentityProvider<T>(path: string, body: unknown, token?: string): Promise<T> {
		const response = await fetch(`${site.backend.idBaseURL}${path}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				...(token !== undefined ? { Authorization: `Bearer ${token}` } : {})
			},
			body: JSON.stringify(body)
		});

		// A gateway error, or a 204, is not JSON — and a parse failure here must surface as the
		// status it belongs to rather than as a `SyntaxError` about the shape of the body.
		const payload = await response.json().catch(() => null);

		if (!response.ok) {
			const message: unknown = payload?.data?.message ?? payload?.error ?? payload?.message;

			throw new Error(
				typeof message === 'string' && message.length > 0
					? message
					: `The sign-in service could not be reached (${response.status}).`
			);
		}

		return (payload?.data ?? payload) as T;
	},

	/**
	 * Asks AbstractID to email a six-digit sign-in code (Design §14).
	 *
	 * The answer says which flow the account actually wants. An account that has chosen a password
	 * or a passkey gets **no code**; the reply names that method instead, and the storefront hands
	 * such a customer to the identity provider's own page rather than reimplementing either here.
	 * `force` overrides that — it is what "email me a code instead" sends.
	 *
	 * An address AbstractID has never seen is *not* an error and is not distinguishable from one it
	 * has: the account is created there when the code is requested, so the storefront never asks
	 * anybody to register and never reveals whether an email is already known.
	 */
	requestEmailOTP(email: string, force: boolean = false): Promise<{ method: 'email' | 'password' | 'passkey' }> {
		return this.postToIdentityProvider(site.backend.id.emailOTPRequest, { email, force });
	},

	/**
	 * Verifies the code and returns the session token, or the pending token a TOTP account needs.
	 *
	 * `requiresTwoFactor` is not a failure — the code was right and the account simply carries a
	 * second factor, so the panel asks for it rather than starting over.
	 */
	verifyEmailOTP(email: string, code: string): Promise<AuthResult> {
		return this.postToIdentityProvider(site.backend.id.emailOTPVerify, { email, code });
	},

	/** Completes a sign-in that stopped at TOTP, exchanging the pending token for a real one. */
	verifyTwoFactor(twoFactorToken: string, code: string): Promise<AuthResult> {
		return this.postToIdentityProvider(site.backend.id.twoFactorVerify, { twoFactorToken, code });
	},

	/**
	 * Registers this customer against this application at AbstractID.
	 *
	 * The consent step of the redirect flow, which the inline flow has no page to perform. The
	 * `returnURL` is this storefront's own origin — AbstractID checks it against the application's
	 * registered verification URLs, which is the same check the redirect flow passes.
	 */
	authorizeApplication(): Promise<unknown> {
		const token = this.getToken();
		if (token === null) throw new Error('Not signed in.');

		return this.postToIdentityProvider(
			site.backend.id.authorize,
			{ appUUID: site.appID, returnURL: site.baseURL },
			token
		);
	},

	initialize(): void {
		// Check for token in URL (callback from AbstractID)
		const searchParams = new URLSearchParams(window.location.search);
		const tokenStringValue = searchParams.get(TOKEN_PARAM);
		if (tokenStringValue && tokenStringValue.length > 0) {
			this.setToken(tokenStringValue);

			// Strip only the token from the URL, so it isn't left in the address
			// bar or leaked via Referer. Any other query parameters belong to the
			// route the user deep-linked to and must survive.
			searchParams.delete(TOKEN_PARAM);
			const remaining = searchParams.toString();
			window.history.replaceState(
				{},
				'',
				`${window.location.pathname}${remaining ? `?${remaining}` : ''}${window.location.hash}`
			);
		}

		const initialToken = this.getToken();

		/**
		 * No usable token. On a public path this resolves to a **signed-out** state and the app
		 * renders; anywhere else it keeps the inherited redirect, which is what guards
		 * `/Account/*` and `/Settings/Admin/*` without a guard in every page.
		 */
		const fnHandleNullToken = () => {
			this.clearToken();
			isAuthorized.set(false);

			if (isPublicPath(window.location.pathname)) {
				isAuthorizationPending.set(false);
				return true;
			}

			this.navigateToLogin();
			return true;
		};

		if (!initialToken) {
			if (!this.tokenData) {
				fnHandleNullToken();
				this.applyTheme();
				return;
			}
			isAuthorized.set(true);
			isAuthorizationPending.set(false);
		} else {
			this.setToken(initialToken);

			if (!this.tokenData) {
				fnHandleNullToken();
				this.applyTheme();
				return;
			}

			// Check expiration
			const expirationDate = new Date(0);
			expirationDate.setUTCSeconds(this.tokenData.exp);

			if (expirationDate < new Date()) {
				fnHandleNullToken();
				this.applyTheme();
				return;
			}

			this.startTokenRefresh(fnHandleNullToken);

			isAuthorized.set(true);
			isAuthorizationPending.set(false);
		}

		this.applyTheme();
	},

	/**
	 * The persisted theme, applied on every exit from `initialize()`.
	 *
	 * It used to sit after the branches, which the early `return`s skipped — so a signed-out
	 * visitor, now a supported state rather than a redirect, would render the storefront with no
	 * theme class at all.
	 */
	applyTheme(): void {
		const savedTheme = localStorage.getItem('color-theme');
		if (!savedTheme) {
			colorTheme.set('dark');
		}
		document.documentElement.classList.toggle('dark', get(colorTheme) === 'dark');
	},

	logout(): void {
		if (this.refreshInterval) {
			clearInterval(this.refreshInterval);
			this.refreshInterval = null;
		}
		this.clearToken();
		isAuthorized.set(false);
		this.navigateToLogin();
	},

	openManagementPortal(): void {
		window.open(`${site.idBaseURL}/manage`, '_blank');
	},

	/**
	 * A GET that attaches the token **when there is one** and omits it otherwise.
	 *
	 * `authenticatedFetch` *throws* without a token, so it cannot serve a `publicAuth` endpoint —
	 * and every public `AppNetwork` function would therefore fail for exactly the visitors the
	 * storefront exists for. Skipping this is the half-done version of the anonymous-browsing
	 * rework the design warns about: everything works while signed in during development and
	 * fails for every real first-time visitor (Design §14).
	 */
	optionalAuthFetch(url: string): Promise<Response> {
		const token = this.getToken();
		return fetch(url, {
			headers: token ? { 'X-AbstractStore-UserToken': token } : {},
			signal: AbortSignal.timeout(30_000)
		});
	},

	// Authenticated fetch helpers
	authenticatedFetch(url: string): Promise<Response> {
		const token = this.getToken();
		if (!token) {
			throw new Error("Cannot perform authenticated fetch (Reason: User is not authenticated.)");
		}
		return fetch(url, {
			headers: {
				'X-AbstractStore-UserToken': token,
			},
			signal: AbortSignal.timeout(30_000)
		});
	},

	authenticatedPost(url: string, contentType: string | undefined, body: BodyInit): Promise<Response> {
		const token = this.getToken();
		if (!token) {
			throw new Error("Cannot perform authenticated fetch (Reason: User is not authenticated.)");
		}

		const headers: Record<string, string> = {
			'X-AbstractStore-UserToken': token,
		};

		if (contentType) {
			headers['Content-Type'] = contentType;
		}

		return fetch(url, {
			method: 'POST',
			headers,
			body,
			signal: AbortSignal.timeout(30_000)
		});
	},

	authenticatedPut(url: string, contentType: string, body: BodyInit): Promise<Response> {
		const token = this.getToken();
		if (!token) {
			throw new Error("Cannot perform authenticated fetch (Reason: User is not authenticated.)");
		}

		const headers: Record<string, string> = {
			'X-AbstractStore-UserToken': token,
		};

		if (contentType) {
			headers['Content-Type'] = contentType;
		}

		return fetch(url, {
			method: 'PUT',
			headers,
			body,
			signal: AbortSignal.timeout(30_000)
		});
	},

	authenticatedDelete(url: string): Promise<Response> {
		const token = this.getToken();
		if (!token) {
			throw new Error("Cannot perform authenticated fetch (Reason: User is not authenticated.)");
		}
		return fetch(url, {
			method: 'DELETE',
			headers: {
				'X-AbstractStore-UserToken': token,
			},
			signal: AbortSignal.timeout(30_000)
		});
	}
};

export default AbstractID;
